import type { TareAppModel } from "../../../shared/native-modules/types/TareBridge";

import { DEFAULT_LOCALE, deserializeTareLocale, type Locale } from "../../../shared/I18n";

/**
 * Check if a locale string is valid.
 * @param localeString - The locale string to check.
 * @returns True if the locale string is valid, false otherwise.
 */
export function isValidLocaleString(
  localeString: string | undefined | null,
): localeString is string {
  if (localeString == null) {
    return false;
  }

  return /^[a-z]{2}(_[A-Z]{2})?$/.test(localeString);
}

/**
 * Deserialize a locale string to a Locale object.
 *
 * Was used in viewjob to receive the locale as a prop before the tare bridge was available.
 * We still use it in case some native clients don't have the tare bridge set up.
 *
 * @param localeString - The locale string to deserialize.
 * @returns The deserialized Locale object.
 */
export function deserializeLocaleProp(localeString: string | undefined | null): Locale {
  if (!isValidLocaleString(localeString)) {
    return DEFAULT_LOCALE;
  }

  const [language, country] = localeString.split("_");
  return { language, country: country ?? null };
}

export function extractLocale(
  appModel: TareAppModel | null | undefined,
  fallbackLocaleProp: string | undefined | null,
): Locale {
  const tareLocale = appModel?.locale;
  const fallbackLocale = deserializeLocaleProp(fallbackLocaleProp);

  if (!tareLocale) {
    return fallbackLocale;
  }

  return deserializeTareLocale(tareLocale) ?? fallbackLocale;
}
